import { useCallback } from "react";
import { useStoryWorkspace } from "../context/StoryWorkspaceContext";
import type {
  MembershipRead,
  TenantWorkspaceRead,
} from "../services/tenantContext/tenantContext.types";
import { setActiveWorkspace } from "../services/workspace/activeWorkspace";

export interface ApplyTenantSelectionArgs {
  tenant: MembershipRead;
  workspaceId: string;
  workspaces: TenantWorkspaceRead[];
}

/**
 * Persists a resolved tenant + workspace choice and hydrates the workspace
 * context from it. Shared by the login "select organization" page and the
 * in-app "Switch organization" modal so both leave the app in the same state.
 *
 * Only the ids are written to localStorage — the workspace list stays in the
 * React Query cache that `useFetchTenantWorkspaces` just populated.
 */
export const useApplyTenantSelection = () => {
  const { setWorkspace, setWorkspaces } = useStoryWorkspace();

  return useCallback(
    ({ tenant, workspaceId, workspaces }: ApplyTenantSelectionArgs) => {
      setActiveWorkspace({ workspaceId, tenantId: tenant.tenant_id });
      const contextWorkspaces = workspaces.map((workspace) => ({
        id: workspace.id,
        name: workspace.name,
        description: workspace.description,
        tenant_id: workspace.tenant_id,
        is_active: workspace.is_active,
      }));
      setWorkspaces(contextWorkspaces);
      setWorkspace(
        contextWorkspaces.find((workspace) => workspace.id === workspaceId) ??
          null,
      );
    },
    [setWorkspace, setWorkspaces],
  );
};
